import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useGetStarted } from "@/components/GetStartedDialog";
import { Award } from "lucide-react";

function SectionLabel({ number, children }) {
  return (
    <div className="flex items-center gap-4">
      <span className="font-ui text-[11px] text-muted-ink tracking-widest">
        {number}
      </span>
      <span className="h-px w-10 bg-[hsl(var(--line-strong))]" />
      <span className="eyebrow">{children}</span>
    </div>
  );
}

function Bullet({ title, body }) {
  return (
    <div className="border-t hairline pt-6">
      <h3 className="font-display text-xl md:text-2xl">
        <span className="text-brand-red mr-2" aria-hidden>—</span>
        {title}
      </h3>
      <p className="mt-3 text-ink-soft leading-relaxed max-w-prose">{body}</p>
    </div>
  );
}

export default function Home() {
  const [items, setItems] = useState([]);
  const { open } = useGetStarted();

  useEffect(() => {
    let mounted = true;
    api
      .get("/portfolio")
      .then((r) => {
        if (!mounted) return;
        const featuredIds = ["riverside-tv", "festival-of-lights", "amber-rdh"];
        const map = new Map(r.data.map((x) => [x.id, x]));
        const featured = featuredIds.map((id) => map.get(id)).filter(Boolean);
        setItems(featured.length ? featured : r.data.slice(0, 3));
      })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        {/* Subtle background image — fades to paper at the bottom */}
        <div
          aria-hidden
          className="absolute inset-0 -z-10 opacity-[0.55]"
          style={{
            backgroundImage: "url('/brand/hero-bg.webp')",
            backgroundSize: "cover",
            backgroundPosition: "center right",
            backgroundRepeat: "no-repeat",
          }}
        />
        <div
          aria-hidden
          className="absolute inset-0 -z-10"
          style={{
            background:
              "linear-gradient(180deg, hsl(var(--paper) / 0.55) 0%, hsl(var(--paper) / 0.75) 40%, hsl(var(--paper) / 0.98) 100%)",
          }}
        />

        <div className="mx-auto max-w-[1360px] px-6 md:px-10 pt-16 md:pt-28 pb-20 md:pb-32">
          <div className="rise">
            <SectionLabel number="01 / Studio">Independent · Custom · Considered</SectionLabel>
          </div>

          <h1
            data-testid="hero-headline"
            className="font-display text-[44px] sm:text-6xl md:text-[86px] lg:text-[104px] leading-[0.98] mt-8 md:mt-10 max-w-[18ch] rise rise-delay-1"
          >
            <em className="not-italic text-brand-red">Unlocking</em>{" "}
            digital experiences that elevate ambitious brands.
          </h1>

          <div className="mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12">
            <p className="md:col-span-7 text-lg md:text-xl leading-relaxed text-ink-soft max-w-prose rise rise-delay-2">
              Redkey Web Design is a boutique studio building custom websites
              for businesses that want more than a template. We serve
              ambitious brands across the Inland Empire, Orange County, and beyond — every site
              is designed, coded, and shipped with intention, from the first
              sketch to the final deploy.
            </p>
            <div className="md:col-span-5 flex flex-col justify-end gap-3 rise rise-delay-3">
              <button
                type="button"
                onClick={open}
                data-testid="hero-cta-primary"
                className="inline-flex items-center justify-between gap-4 bg-ink text-paper px-6 py-4 hover:bg-electric transition-colors duration-200"
              >
                <span className="font-ui text-base">Get Started</span>
                <span aria-hidden>→</span>
              </button>
              <Link
                to="/work"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center justify-between gap-4 border hairline-strong text-ink px-6 py-4 hover:border-ink transition-colors duration-200 bg-paper/60 backdrop-blur-sm"
              >
                <span className="font-ui text-base">See selected work</span>
                <span aria-hidden>→</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* WHY CUSTOM */}
      <section className="border-t hairline bg-paper-alt">
        <div className="mx-auto max-w-[1360px] px-6 md:px-10 py-20 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
          <div className="md:col-span-4">
            <SectionLabel number="02 / Approach">The Case for Custom</SectionLabel>
            <h2 className="font-display text-4xl md:text-5xl mt-6">
              Why Choose Custom Web Development?
            </h2>
          </div>
          <div className="md:col-span-8 md:pt-2">
            <div className="max-w-prose space-y-6 text-ink-soft text-lg leading-relaxed font-body">
              <p>
                Your website is more than just an online presence — it&rsquo;s
                the first impression of your business and one of your most
                valuable marketing tools. We specialize in designing and
                developing custom websites that are built specifically for
                your brand, your goals, and your customers.
              </p>
              <p>
                Rather than relying on one-size-fits-all templates, we create
                websites that are visually distinctive, fast, responsive, and
                strategically designed to help your business grow. Every
                project is crafted with attention to detail, ensuring your
                website not only looks exceptional but also provides an
                intuitive user experience that turns visitors into loyal
                customers.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* EIGHT REASONS */}
      <section className="border-t hairline">
        <div className="mx-auto max-w-[1360px] px-6 md:px-10 py-20 md:py-32">
          <div className="max-w-3xl">
            <SectionLabel number="03 / Difference">The Difference</SectionLabel>
            <h2 className="font-display text-4xl md:text-6xl mt-6">
              Why Work With Us Instead of a Template Website?
            </h2>
            <p className="mt-6 text-lg text-ink-soft max-w-prose">
              Template platforms serve a purpose. But if your business depends
              on standing out, converting well, and scaling gracefully, a
              custom build pays for itself. Here&rsquo;s what that looks like.
            </p>
          </div>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-10">
            <Bullet title="Custom Designed for Your Brand" body="Your website is built from the ground up to reflect your business — not adapted from a generic template used by hundreds of others." />
            <Bullet title="Designed to Convert" body="Every page is thoughtfully structured with your customers in mind, encouraging inquiries, bookings, and sales." />
            <Bullet title="Fast & Optimized" body="Clean, efficient code results in quicker load times, improved performance, and a better user experience." />
            <Bullet title="Built to Grow" body="As your business evolves, your website can easily expand with new features, services, and functionality — without starting over." />
            <Bullet title="Mobile-First Experience" body="Your website will look and perform beautifully on desktops, tablets, and smartphones." />
            <Bullet title="SEO-Friendly Foundation" body="Every site is built with best practices that help search engines understand your content and improve your online visibility." />
            <Bullet title="Personalized Service" body="You'll work directly with us throughout the entire process — ensuring clear communication, faster revisions, and a website that truly reflects your vision." />
            <Bullet title="No Cookie-Cutter Designs" body="Every website we create is unique, giving your business a professional edge that helps you stand out from the competition." />
          </div>
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="border-t hairline bg-ink text-paper">
        <div className="mx-auto max-w-[1360px] px-6 md:px-10 py-24 md:py-36 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-7">
            <p className="font-ui text-[11px] tracking-widest text-paper/50">
              04 / MANIFESTO
            </p>
            <h2 className="font-display text-4xl md:text-6xl mt-6 text-paper">
              Your Website Should Work{" "}
              <em className="not-italic text-electric">As Hard As You Do.</em>
            </h2>
          </div>
          <div className="md:col-span-5 md:pt-4">
            <p className="text-lg leading-relaxed text-paper/80 font-body max-w-prose">
              We don&rsquo;t just build websites — we create digital
              experiences that represent your business with professionalism,
              credibility, and purpose. Whether you&rsquo;re launching a new
              brand or elevating an existing one, our goal is to deliver a
              website that is beautiful, functional, and built to support
              your long-term success.
            </p>
            <button
              type="button"
              onClick={open}
              data-testid="manifesto-cta"
              className="inline-flex items-center gap-3 mt-8 font-ui text-lg text-paper link-underline"
            >
              Get Started
              <span className="text-brand-red" aria-hidden>→</span>
            </button>
          </div>
        </div>
      </section>

      {/* FEATURED WORK */}
      <section className="border-t hairline">
        <div className="mx-auto max-w-[1360px] px-6 md:px-10 py-20 md:py-32">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
            <div>
              <SectionLabel number="05 / What we do">Four disciplines</SectionLabel>
              <h2 className="font-display text-4xl md:text-5xl mt-6 max-w-[24ch]">
                Custom web development, technical SEO, AI automation, and Drupal.
              </h2>
            </div>
            <Link
              to="/services"
              data-testid="see-all-services"
              className="font-ui text-ink link-underline"
            >
              Explore services →
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 mb-24">
            {[
              { slug: "web-design", name: "Web Design & Development", body: "Custom, responsive builds — no templates. Fast, accessible, hand-coded." },
              { slug: "seo", name: "SEO & Digital Growth", body: "Technical SEO, structured data, and content strategy that compounds." },
              { slug: "ai-automation", name: "AI Automation", body: "Lead qualification, content workflows, and process automation that actually earns its keep." },
              { slug: "drupal-government", name: "Drupal, WordPress & Government", body: "Enterprise Drupal, WordPress, and custom CMS builds for public-sector and enterprise. WCAG 2.2 baked in." },
            ].map((s) => (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                data-testid={`home-service-${s.slug}`}
                className="border hairline-strong bg-paper p-6 hover:border-ink transition-colors group block"
              >
                <p className="eyebrow">Service</p>
                <h3 className="font-display text-2xl mt-3 group-hover:text-electric transition-colors">
                  {s.name}
                </h3>
                <p className="mt-3 font-body text-ink-soft leading-relaxed text-sm">
                  {s.body}
                </p>
                <span className="mt-4 inline-flex items-center gap-1 font-ui text-sm text-brand-red-ink">
                  Learn more <span aria-hidden>→</span>
                </span>
              </Link>
            ))}
          </div>

          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
            <div>
              <SectionLabel number="06 / Recent">Selected work</SectionLabel>
              <h2 className="font-display text-4xl md:text-5xl mt-6">
                A few things we&rsquo;ve made lately.
              </h2>
            </div>
            <Link
              to="/work"
              data-testid="see-all-work"
              className="font-ui text-ink link-underline"
            >
              View all work →
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
            {items.map((it, idx) => (
              <Link
                key={it.id}
                to={`/work/${it.id}`}
                data-testid={`home-portfolio-${it.id}`}
                className="portfolio-card group block"
              >
                <div className="aspect-[4/5] overflow-hidden bg-paper-alt border hairline relative">
                  <img
                    src={it.image}
                    alt={`${it.title} — case study cover`}
                    width="1000"
                    height="1250"
                    loading={idx === 0 ? "eager" : "lazy"}
                    decoding="async"
                    className="portfolio-img w-full h-full object-cover"
                  />
                  {it.award && (
                    <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-paper/95 border border-brand-red text-brand-red px-2.5 py-1 font-ui text-[10px] tracking-[0.16em] uppercase">
                      <Award size={11} strokeWidth={2} />
                      {it.award}
                    </span>
                  )}
                </div>
                <div className="mt-4 flex items-baseline justify-between gap-4">
                  <div>
                    <p className="eyebrow">{it.category}</p>
                    <h3 className="font-display text-2xl mt-1 group-hover:text-electric transition-colors">
                      {it.title}
                    </h3>
                  </div>
                  <span className="font-ui text-[11px] text-label">
                    {it.year}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t hairline bg-paper-alt" data-testid="home-faq">
        <div className="mx-auto max-w-[1360px] px-6 md:px-10 py-20 md:py-28">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-4">
              <p className="eyebrow">§ FAQ</p>
              <h2 className="font-display text-4xl md:text-5xl mt-4">
                Common questions.
              </h2>
              <p className="mt-4 text-ink-soft leading-relaxed max-w-prose">
                Answers to the questions we hear most from founders,
                marketing leads, and government stakeholders. Have a different
                question?{" "}
                <button type="button" onClick={open} className="text-electric-ink link-underline">
                  Ask directly
                </button>.
              </p>
            </div>
            <dl className="md:col-span-8 divide-y divide-[hsl(var(--line-strong))] border-t border-b border-[hsl(var(--line-strong))]">
              {[
                {
                  q: "How long does a custom website take to build?",
                  a: "Most marketing sites ship in 4–8 weeks; e-commerce and Drupal builds run 8–14 weeks depending on scope, content readiness, and integrations. Every project starts with a written scope, timeline, and price so there are no surprises.",
                },
                {
                  q: "Do you build Drupal websites for government agencies?",
                  a: "Yes. We specialize in Drupal 10 development, migrations, and multi-department government websites — including WCAG 2.2 accessibility, Section 508 compliance, editor training, and multi-year maintenance retainers.",
                },
                {
                  q: "Can you improve our search visibility without a redesign?",
                  a: "Often, yes. Our technical SEO engagements audit site architecture, structured data, Core Web Vitals, and content — then remediate the issues search engines actually penalize. Redesigns happen only when the current site can't be optimized further.",
                },
                {
                  q: "What does 'AI automation' actually mean for a small business?",
                  a: "Specific, measurable workflows: qualifying inbound leads before they hit your inbox, drafting first-pass content that a marketer edits, triaging customer support tickets, and automating repetitive back-office tasks. We only recommend AI where it earns its keep — never for its own sake.",
                },
                {
                  q: "Are all Redkey Web Design sites accessible?",
                  a: "Yes. Every site we build targets WCAG 2.2 AA at minimum, with government and healthcare projects pushed to AAA where feasible. Accessibility is baked into the design system, not audited after the fact.",
                },
                {
                  q: "Do you offer ongoing website maintenance?",
                  a: "Yes — long-term partnerships are the model. Monthly retainers cover security updates, accessibility drift, content refreshes, performance monitoring, and continuous improvement. Launch-and-leave isn't how we work.",
                },
                {
                  q: "Where is Redkey Web Design based?",
                  a: "We work remotely with clients across the United States. All development, design, and communication is handled by a senior developer with 15+ years of experience — never outsourced.",
                },
              ].map((f, i) => (
                <div key={f.q} className="py-6" data-testid={`faq-${i}`}>
                  <dt className="font-display text-xl md:text-2xl text-ink">{f.q}</dt>
                  <dd className="mt-3 font-body text-ink-soft leading-relaxed max-w-prose">{f.a}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </section>
    </div>
  );
}
